import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import SweetAlertService from "../../../services/SweetAlertService";
import { errorMessages, getMaxLengthErrorMessage, getRequiredErrorMessage, jobCategoryNameMaxLength, jobCategoryActivityNamePattern, errorMessagesForSubRoleSelection } from "../Shared/Constants/AdminModuleConstants";
import JobCategoryService from "../Shared/Services/JobCategoryService";
import { SubRole } from "./models/JobCategory";

export const CreateJobCategory = () => {
    const navigate = useNavigate();
    const [subRoles, setSubRoles] = useState<SubRole[]>([]);
    const [selectedSubRoles, setSelectedSubRoles] = useState<number[]>([]);
    const { register, handleSubmit, reset, formState: { errors } } = useForm(
        { defaultValues: { name: "", shortName: "", isCountToSession: "false", isDefaultInWorkActivity: "false", subRoleIds: "" } }
    );

    useEffect(() => {
        loadSubRoles();
    }, []);

    const loadSubRoles = async () => {
        try {
            const response = await JobCategoryService.getAllSubRoles();
            setSubRoles(response?.data);
        } catch (error) {
            SweetAlertService.error("Error", "Error loading subroles");
        }
    };

    const handleSubRoleChange = (subRoleId: number, isChecked: boolean) => {
        if (isChecked) {
            setSelectedSubRoles([...selectedSubRoles, subRoleId]);
        } else {
            setSelectedSubRoles(selectedSubRoles.filter(id => id !== subRoleId));
        }
    };

    const formSubmit = (data: any) => {
        data.name = data.name.trim();
        data.shortName = data.shortName.trim();
        data.isCountToSession = data.isCountToSession == 'true';
        data.isDefaultInWorkActivity = data.isDefaultInWorkActivity == 'true';
        data.subRoleIds = selectedSubRoles;
        JobCategoryService.addJobCategory(data).then((response) => {
            if (response.data > 0) {
                navigate("/jobcategory");
            } else {
                SweetAlertService.error("Error", "Something went wrong while adding job category");
            }
            reset();
            setSelectedSubRoles([]);
        }).catch(error => {
            SweetAlertService.error("Error", error.response.data.errors.Name[0]);
        })
    };

    return (
        <div className="card card-default">
            <div className="card-header">
                <h4>Create Job Category</h4>
            </div>
            <div className="card-body">
                <form onSubmit={handleSubmit(formSubmit)}>
                    <div className="form-group row">
                        <label className="col-lg-3 control-label">Name</label>
                        <div className="col-lg-9">
                            <input
                                type="text"
                                className="form-control"
                                {...register("name", {
                                    required: getRequiredErrorMessage("Name"),
                                    maxLength: {
                                        value: jobCategoryNameMaxLength,
                                        message: getMaxLengthErrorMessage(jobCategoryNameMaxLength)
                                    },
                                    pattern: {
                                        value: jobCategoryActivityNamePattern,
                                        message: errorMessages.pattern
                                    },
                                })}
                            />
                            {errors.name && (
                                <span className="help-block m-b-none text-danger field-validation-valid">
                                    {errors.name.message?.toString()}
                                </span>
                            )}
                        </div>
                    </div>
                    <br/>
                    <div className="form-group row">
                        <label className="col-lg-3 control-label">Short Name</label>
                        <div className="col-lg-9">
                            <input
                                type="text"
                                className="form-control"
                                {...register("shortName", {
                                    required: getRequiredErrorMessage("Short Name"),
                                    maxLength: {
                                        value: jobCategoryNameMaxLength,
                                        message: getMaxLengthErrorMessage(jobCategoryNameMaxLength)
                                    },
                                    pattern: {
                                        value: jobCategoryActivityNamePattern,
                                        message: errorMessages.pattern
                                    }
                                })}
                            />
                            {errors.shortName && (
                                <span className="help-block m-b-none text-danger field-validation-valid">
                                    {errors.shortName.message?.toString()}
                                </span>
                            )}
                        </div>
                    </div>
                    <br/>
                    <div className="form-group row">
                        <label className="col-lg-3 control-label">Count To Sessions</label>
                        <div className="col-lg-9">
                            <div className="d-flex flex-row">
                                <input
                                    value="true"
                                    type="radio"
                                    {...register("isCountToSession")}
                                />
                                Yes
                                <input className="ms-3"
                                    value="false"
                                    type="radio"
                                    {...register("isCountToSession")}
                                />No
                            </div>
                        </div>
                    </div>
                    <br/>
                    <div className="form-group row">
                        <label className="col-lg-3 control-label">Is Default In WorkActivity</label>
                        <div className="col-lg-9">
                            <div className="d-flex flex-row">
                                <input
                                    value="true"
                                    type="radio"
                                    {...register("isDefaultInWorkActivity")} />
                                Yes
                                <input className="ms-3"
                                    value="false"
                                    type="radio"
                                    {...register("isDefaultInWorkActivity")}
                                />No
                            </div>
                        </div>
                    </div>
                    <br/>
                    <div className="form-group row">
                        <label className="col-lg-3 control-label">Assign Roles</label>
                        <div className="col-lg-9">
                            <div className="d-flex flex-column">
                                <input
                                    type="hidden"
                                    {...register("subRoleIds", {
                                        validate: () => selectedSubRoles?.length > 0 ? true : errorMessagesForSubRoleSelection
                                    })}
                                    value={selectedSubRoles.join(',')}
                                />
                                {subRoles.map((subRole) => (
                                    <span key={subRole.id} className="text">
                                        <input
                                            className="checkbox"
                                            type="checkbox"
                                            id={`subRole_${subRole?.id}`}
                                            checked={selectedSubRoles.includes(subRole?.id)}
                                            onChange={(e) => handleSubRoleChange(subRole?.id, e?.target?.checked)}
                                        />{' '}
                                        {subRole?.name}
                                    </span>
                                ))}
                            </div>
                            {errors?.subRoleIds && (
                                <span className="help-block m-b-none text-danger field-validation-valid">
                                    {errors?.subRoleIds?.message?.toString()}
                                </span>
                            )}
                        </div>
                    </div>
                    <br/>
                    <div className="form-group row">
                        <div className=" col-sm-offset-3 px-2">
                            <a className="btn btn-secondary" onClick={() => navigate('/jobcategory')}>
                                Cancel
                            </a>
                            <button className="btn btn-primary ms-1" type="submit">
                                Create
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}
export default CreateJobCategory;